import IApprovement from '../Models/IApprovement';
import IBoard from '../Models/IBoard';
import IColumn from '../Models/IColumn';
import INumeric from '../Models/INumeric';
import INumericCounter from '../Models/INumericCounter';
import INumericCounterValue from '../Models/INumericCounterValue';
import IProject from '../Models/IProject';
import IStory from '../Models/IStory';
import ITask from '../Models/ITask';
import IAttachment from '../Values/IAttachment';
import TaskType from '../Values/TaskType';
import Permissions from './Permissions';

export interface IOperation<TResource = unknown> {
    authorOnly?: boolean;
    getAuthorId?: (resource: TResource) => string | number | undefined;
    requiredPermission?: (resource: TResource) => string;
}

const Operations = {
    UI: {
        ListTasks: {
            requiredPermission: () => Permissions.UI.CanListTasks,
        },
        ListNumerics: {
            requiredPermission: () => Permissions.UI.CanListNumerics,
        },
    },

    Numerics: {
        Read: {
            requiredPermission: () => Permissions.Numerics.CanRead,
        } as IOperation<INumeric>,

        Create: {
            requiredPermission: () => Permissions.Numerics.CanCreate,
        } as IOperation<INumeric>,

        Delete: {
            requiredPermission: () => Permissions.Numerics.CanDelete,
        } as IOperation<INumeric>,

        UpdatePublicFields: {
            requiredPermission: () => Permissions.Numerics.CanUpdatePublicFields,
        } as IOperation<INumeric>,

        UpdatePrivateFields: {
            requiredPermission: () => Permissions.Numerics.CanUpdatePrivateFields,
        } as IOperation<INumeric>,

        Archive: {
            authorOnly: true,
            requiredPermission: () => Permissions.Numerics.CanDelete,
            getAuthorId: (resource: INumeric) => resource?.authorId,
        } as IOperation<INumeric>,
    },

    NumericCounters: {
        Read: {
            requiredPermission: () => Permissions.NumericCounters.CanRead,
        } as IOperation<INumericCounter>,

        Create: {
            requiredPermission: () => Permissions.NumericCounters.CanCreate,
        } as IOperation<INumericCounter>,

        Delete: {
            requiredPermission: () => Permissions.NumericCounters.CanDelete,
        } as IOperation<INumericCounter>,

        UpdatePublicFields: {
            requiredPermission: () => Permissions.NumericCounters.CanUpdatePublicFields,
        } as IOperation<INumericCounter>,

        UpdatePrivateFields: {
            requiredPermission: () => Permissions.NumericCounters.CanUpdatePrivateFields,
        } as IOperation<INumericCounter>,
    },

    NumericCounterValues: {
        Read: {
            requiredPermission: () => Permissions.NumericCounterValues.CanRead,
        } as IOperation<INumericCounterValue>,

        Create: {
            requiredPermission: () => Permissions.NumericCounterValues.CanCreate,
        } as IOperation<INumericCounterValue>,
    },

    Columns: {
        Read: {
            requiredPermission: () => Permissions.TaskManagement.CanRead,
        } as IOperation<IColumn>,

        Create: {
            requiredPermission: () => Permissions.Columns.CanCreate,
        } as IOperation<IColumn>,

        Update: {
            requiredPermission: () => Permissions.Columns.CanUpdate,
        } as IOperation<IColumn>,

        Delete: {
            requiredPermission: () => Permissions.Columns.CanDelete,
        } as IOperation<IColumn>,
    },

    Projects: {
        Read: {
            requiredPermission: () => Permissions.TaskManagement.CanRead,
        } as IOperation<IProject>,
    },

    Boards: {
        Read: {
            requiredPermission: () => Permissions.TaskManagement.CanRead,
        } as IOperation<IBoard>,
    },

    Comments: {
        Update: {
            authorOnly: true,
            getAuthorId: (resource: IStory) => resource?.actorId,
        } as IOperation<IStory>,

        Delete: {
            authorOnly: true,
            getAuthorId: (resource: IStory | IAttachment) =>
                (resource as IStory & IAttachment)?.actorId || (resource as IStory & IAttachment)?.authorId,
        } as IOperation<IStory | IAttachment>,
    },

    Tasks: {
        Read: {
            requiredPermission: () => Permissions.TaskManagement.CanRead,
        } as IOperation,

        Create: {
            requiredPermission: (resource: Partial<ITask>) =>
                resource.type === TaskType.Goal ? Permissions.Goals.CanCreate : Permissions.Tasks.CanCreate,
        } as IOperation<Partial<ITask>>,

        Update: {
            requiredPermission: (resource: ITask) =>
                resource.type === TaskType.Goal ? Permissions.Goals.CanUpdate : Permissions.Tasks.CanUpdate,
        } as IOperation<ITask>,

        UpdateAuthor: {
            requiredPermission: () => Permissions.CanAll,
        } as IOperation,

        Archive: {
            authorOnly: true,
            getAuthorId: (resource: ITask) => resource?.authorId,
            requiredPermission: (resource) =>
                (resource as ITask).type === TaskType.Goal ? Permissions.Goals.CanDelete : Permissions.Tasks.CanDelete,
        } as IOperation<ITask>,

        Finish: {
            authorOnly: true,
            getAuthorId: (resource: ITask) => resource?.authorId,
            requiredPermission: (resource) =>
                (resource as ITask).type === TaskType.Goal ? Permissions.Goals.CanUpdate : Permissions.Tasks.CanUpdate,
        } as IOperation<ITask>,

        ReturnToInProgress: {
            authorOnly: true,
            getAuthorId: (resource: ITask) => resource?.authorId,
            requiredPermission: (resource) =>
                (resource as ITask).type === TaskType.Goal ? Permissions.Goals.CanUpdate : Permissions.Tasks.CanUpdate,
        } as IOperation<ITask>,

        RollbackChanges: {
            requiredPermission: (resource) =>
                (resource as ITask).type === TaskType.Goal ? Permissions.Goals.CanUpdate : Permissions.Tasks.CanUpdate,
        } as IOperation<ITask>,

        Complete: {
            requiredPermission: (resource) =>
                (resource as ITask).type === TaskType.Goal
                    ? Permissions.Goals.CanComplete
                    : Permissions.Tasks.CanComplete,
        } as IOperation,

        AddCollaborator: {
            requiredPermission: (resource) =>
                (resource as ITask).type === TaskType.Goal ? Permissions.Goals.CanUpdate : Permissions.Tasks.CanUpdate,
        } as IOperation,

        RemoveCollaborator: {
            requiredPermission: (resource) =>
                (resource as ITask).type === TaskType.Goal ? Permissions.Goals.CanUpdate : Permissions.Tasks.CanUpdate,
        } as IOperation,

        StartApprovementProcess: {
            requiredPermission: (resource) =>
                (resource as ITask).type === TaskType.Goal ? Permissions.Goals.CanUpdate : Permissions.Tasks.CanUpdate,
        } as IOperation<ITask>,

        Approve: {
            authorOnly: true,
            getAuthorId: (resource: IApprovement) => resource?.approverId,
        } as IOperation<IApprovement>,

        Disapprove: {
            authorOnly: true,
            getAuthorId: (resource: IApprovement) => resource?.approverId,
        } as IOperation<IApprovement>,

        Comment: {
            requiredPermission: (resource) =>
                resource.type === TaskType.Goal ? Permissions.Goals.CanComment : Permissions.Tasks.CanComment,
        } as IOperation<ITask>,
    },
};

export default Operations;
